'use client';

import {
  addDays,
  addMonths,
  addYears,
  endOfYear,
  format,
  getDay,
  getMonth,
  getYear,
  isSameDay,
  isValid,
  setYear,
  startOfToday,
  startOfYear,
} from 'date-fns';
import {
  type ChangeEvent,
  type Dispatch,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useControllableState } from '../../../hooks/use-controllable-state';
import {
  getFirstAvailableDate,
  getMonthMatrix,
  getNextAvailableDay,
  getParsedDate,
  getPreviousAvailableDay,
  getValidYear,
  isDateInBetween,
  isDisabledDate,
} from '../../../utils/dates';
import { getArrayFromRange } from '../../../utils/get-array-from-range';
import type { DatePickerRootPropsInterface } from '../../date-picker';
import { CALENDAR_CHANGE_REASON } from '../constants/calendar-change-reason';
import { CALENDAR_MONTHS } from '../constants/calendar-months';
import type { CalendarRootPropsInterface } from '../types/calendar-root.types';

export function useCalendar(
  props: Partial<CalendarRootPropsInterface> & Pick<DatePickerRootPropsInterface, 'onValueChange'>,
) {
  const {
    selected,
    defaultSelected,
    onSelectedChange,
    onValueChange,
    disabledDates = [],
    minDate,
    maxDate,
    dateFormat = 'MM/dd/yyyy',
  } = props;

  const headerId = useId();

  const focusDateRef = useRef<HTMLButtonElement>(null);

  const [valueState, setValueState] = useControllableState<string | Date>({
    prop: selected,
    defaultProp: defaultSelected,
  }) as [string | Date, Dispatch<SetStateAction<string | Date>>];

  const parsedSelectedDate = useMemo(
    () => getParsedDate({ date: valueState, dateFormat }),
    [valueState, dateFormat],
  );

  const parsedMinDate = useMemo(() => {
    const parsedDate = getParsedDate({ date: minDate, dateFormat });
    if (isValid(parsedDate)) {
      return parsedDate;
    }
    // Given min date is invalid, default to 5 years before
    const fallbackDate = isValid(parsedSelectedDate) ? parsedSelectedDate : startOfToday();

    return startOfYear(addYears(fallbackDate as Date, -5));
  }, [dateFormat, minDate, parsedSelectedDate]);

  const parsedMaxDate = useMemo(() => {
    const parsedDate = getParsedDate({ date: maxDate, dateFormat });
    if (isValid(parsedDate)) {
      return parsedDate;
    }
    // Given max date is invalid, default to 5 years after
    const fallbackDate = isValid(parsedSelectedDate) ? parsedSelectedDate : startOfToday();

    return endOfYear(addYears(fallbackDate as Date, 5));
  }, [dateFormat, maxDate, parsedSelectedDate]);

  const parsedDisabledDates = useMemo(() => {
    if (typeof disabledDates === 'function') {
      return disabledDates;
    }

    return disabledDates.map((date: Date | string) => getParsedDate({ date, dateFormat }));
  }, [dateFormat, disabledDates]);

  const initialFocusDate = useMemo(() => {
    return getFirstAvailableDate(
      parsedSelectedDate,
      parsedDisabledDates,
      parsedMinDate,
      parsedMaxDate,
    );
  }, [parsedDisabledDates, parsedMaxDate, parsedMinDate, parsedSelectedDate]);

  const [focusDate, setFocusDate] = useState(initialFocusDate);
  const [dayFocusable, setDayFocusable] = useState(false);

  const month = useMemo(() => getMonth(focusDate), [focusDate]);
  const minYear = useMemo(() => getYear(parsedMinDate), [parsedMinDate]);
  const maxYear = useMemo(() => getYear(parsedMaxDate), [parsedMaxDate]);
  const year = useMemo(
    () => getValidYear(focusDate, minYear, maxYear),
    [focusDate, maxYear, minYear],
  );

  const monthMatrix = useMemo(() => getMonthMatrix(month, year), [month, year]);

  const yearRange = useMemo(() => getArrayFromRange(minYear, maxYear, 1), [maxYear, minYear]);

  const isNextMonthDisabled = useMemo(
    () => year === maxYear && month >= getMonth(parsedMaxDate),
    [year, month, maxYear, parsedMaxDate],
  );

  const isPrevMonthDisabled = useMemo(
    () => year === minYear && month <= getMonth(parsedMinDate),
    [year, month, minYear, parsedMinDate],
  );

  useEffect(() => {
    if (focusDateRef.current && dayFocusable) {
      focusDateRef.current.focus();
    }
  }, [dayFocusable, focusDate, focusDateRef]);

  const updateFocusDate = useCallback(
    (newDate: Date | null, isDayFocusable = true, isPageKeyCode = false) => {
      if (!newDate) return;

      // keyboard day events and new date is out of range, ignore
      if (
        isDayFocusable &&
        !(isPageKeyCode || isDateInBetween(newDate, parsedMinDate, parsedMaxDate))
      ) {
        return;
      }

      let date = newDate;
      // mouse events or keyboard page events and new date is disabled
      // find the first available day
      if (!isDayFocusable || isPageKeyCode) {
        date = getFirstAvailableDate(date, parsedDisabledDates, parsedMinDate, parsedMaxDate);
      }
      setFocusDate(date);
      setDayFocusable(isDayFocusable);
    },
    [parsedDisabledDates, parsedMaxDate, parsedMinDate],
  );

  const updateSelectedDate = useCallback(
    (date: Date, meta: { reason: string }) => {
      const formattedDate = format(date, dateFormat);
      setValueState(formattedDate);

      onSelectedChange?.(
        {
          date,
          formatted: formattedDate,
        },
        meta,
      );
      onValueChange?.(formattedDate, meta);
    },
    [dateFormat, onSelectedChange, onValueChange, setValueState],
  );

  const onDayClickCallback = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const target = event.currentTarget;
      const day = Number(target.getAttribute('data-day'));
      const newDate = new Date(year, month, day);
      updateSelectedDate(newDate, {
        reason: CALENDAR_CHANGE_REASON.dropdownClick,
      });
    },
    [month, year, updateSelectedDate],
  );

  const onDayFocusCallback = useCallback(
    (event: FocusEvent<HTMLButtonElement>) => {
      const target = event.currentTarget;
      const day = Number(target.getAttribute('data-day'));
      setFocusDate(new Date(year, month, day));
    },
    [month, year],
  );

  const onDayKeyDownCallback = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      let newDate;

      switch (e.key) {
        case 'Enter':
        case 'Space': {
          newDate = focusDate;
          updateSelectedDate(newDate, {
            reason: CALENDAR_CHANGE_REASON.dropdownKeyboard,
          });
          break;
        }

        case 'ArrowRight': {
          newDate = getNextAvailableDay(
            focusDate,
            parsedDisabledDates,
            parsedMinDate,
            parsedMaxDate,
          );
          if (newDate) {
            updateFocusDate(newDate);
          }
          break;
        }

        case 'ArrowLeft': {
          newDate = getPreviousAvailableDay(
            focusDate,
            parsedDisabledDates,
            parsedMinDate,
            parsedMaxDate,
          );
          updateFocusDate(newDate);
          break;
        }

        case 'ArrowDown': {
          newDate = addDays(focusDate, 7);
          updateFocusDate(newDate);
          break;
        }

        case 'ArrowUp': {
          newDate = addDays(focusDate, -7);
          updateFocusDate(newDate);
          break;
        }

        case 'PageUp': {
          newDate = addMonths(focusDate, -1);
          updateFocusDate(newDate, true, true);
          break;
        }

        case 'PageDown': {
          newDate = addMonths(focusDate, 1);
          updateFocusDate(newDate, true, true);
          break;
        }

        case 'Home': {
          newDate = addDays(focusDate, -getDay(focusDate));
          updateFocusDate(newDate);
          break;
        }

        case 'End': {
          newDate = addDays(focusDate, 6 - getDay(focusDate));
          updateFocusDate(newDate);
          break;
        }

        default: {
          break;
        }
      }

      if (newDate) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    [
      focusDate,
      updateFocusDate,
      updateSelectedDate,
      parsedDisabledDates,
      parsedMinDate,
      parsedMaxDate,
    ],
  );

  const onPrevMonthCallback = useCallback(() => {
    const newDate = addMonths(focusDate, -1);
    updateFocusDate(newDate, false);
  }, [focusDate, updateFocusDate]);

  const onNextMonthCallback = useCallback(() => {
    const newDate = addMonths(focusDate, 1);
    updateFocusDate(newDate, false);
  }, [focusDate, updateFocusDate]);

  const onYearChangeCallback = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const newDate = setYear(focusDate, Number(e.target.value));
      updateFocusDate(newDate, false);
    },
    [focusDate, updateFocusDate],
  );

  const getDayProps = useCallback(
    (day: number) => {
      const currentDate = new Date(year, month, day);
      const today = isSameDay(currentDate, startOfToday());
      const selected = parsedSelectedDate ? isSameDay(currentDate, parsedSelectedDate) : false;
      const focus = isSameDay(currentDate, focusDate);
      const disabled = isDisabledDate(
        currentDate,
        parsedDisabledDates,
        parsedMinDate,
        parsedMaxDate,
      );

      return {
        'tabIndex': focus ? 0 : -1,
        selected,
        day,
        disabled,
        today,
        'data-day': day,
        'ref': focus ? focusDateRef : undefined,
        'onClick': onDayClickCallback,
        'onFocus': onDayFocusCallback,
        'onKeyDown': onDayKeyDownCallback,
      };
    },
    [
      parsedSelectedDate,
      focusDate,
      parsedMinDate,
      parsedMaxDate,
      month,
      year,
      parsedDisabledDates,
      onDayClickCallback,
      onDayFocusCallback,
      onDayKeyDownCallback,
    ],
  );

  const getNextMonthProps = useCallback(() => {
    return {
      onClick: onNextMonthCallback,
      disabled: isNextMonthDisabled,
    };
  }, [onNextMonthCallback, isNextMonthDisabled]);

  const getPreviousMonthProps = useCallback(() => {
    return {
      onClick: onPrevMonthCallback,
      disabled: isPrevMonthDisabled,
    };
  }, [onPrevMonthCallback, isPrevMonthDisabled]);

  const getYearSelectProps = useCallback(() => {
    return {
      onChange: onYearChangeCallback,
      value: year,
    };
  }, [onYearChangeCallback, year]);

  return useMemo(
    () => ({
      dateFormat,
      value: valueState,
      monthMatrix,
      initialFocusDate,
      currentMonth: CALENDAR_MONTHS[month],
      currentYear: year,
      yearRange,
      headerId: headerId,

      getDayProps,
      getNextMonthProps,
      getPreviousMonthProps,
      getYearSelectProps,
      setValue: setValueState,
    }),
    [
      dateFormat,
      valueState,
      monthMatrix,
      setValueState,
      month,
      year,
      yearRange,
      getDayProps,
      getPreviousMonthProps,
      getNextMonthProps,
      getYearSelectProps,
      initialFocusDate,
      headerId,
    ],
  );
}
